import { useState, useRef } from "react"
import {Printer, Share2, ChevronDown, ChevronUp, Check} from 'lucide-react'
import type { LoanResults } from "@/utils/calculator"
import { formatCurrency } from "@/utils/calculator"

interface AmortizationTableProps {
  results: LoanResults
}

export default function AmortizationTable({ results }: AmortizationTableProps) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const tableRef = useRef<HTMLDivElement>(null)

  const visibleRows = expanded ? results.schedule : results.schedule.slice(0, 12)

  const handlePrint = () => {
    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    const rows = results.schedule
      .map(
        (row) => `
      <tr>
        <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:center">${row.month}</td>
        <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:right">${formatCurrency(row.payment)}</td>
        <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:right">${formatCurrency(row.principal)}</td>
        <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:right">${formatCurrency(row.interest)}</td>
        ${row.extraPayment > 0 ? `<td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:right">${formatCurrency(row.extraPayment)}</td>` : `<td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:right">$0.00</td>`}
        <td style="padding:8px;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:600">${formatCurrency(row.balance)}</td>
      </tr>`
      )
      .join("")

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Land Loan Amortization Schedule - LandMortgageCalculator.com</title>
        <style>
          body { font-family: 'Segoe UI', sans-serif; padding: 40px; color: #1e293b; }
          h1 { color: #1B4332; font-size: 24px; margin-bottom: 8px; }
          .summary { color: #64748b; margin-bottom: 24px; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { background: #1B4332; color: white; padding: 10px 8px; text-align: right; }
          th:first-child { text-align: center; border-radius: 8px 0 0 0; }
          th:last-child { border-radius: 0 8px 0 0; }
          tr:nth-child(even) { background: #f8fafc; }
          .footer { margin-top: 24px; font-size: 12px; color: #94a3b8; text-align: center; }
        </style>
      </head>
      <body>
        <h1>Land Loan Amortization Schedule</h1>
        <p class="summary">
          Loan Amount: ${formatCurrency(results.loanAmount)} | 
          Monthly Payment: ${formatCurrency(results.monthlyPayment)} | 
          Total Interest: ${formatCurrency(results.totalInterest)} |
          Payoff: ${results.payoffDate}
        </p>
        <table>
          <thead>
            <tr>
              <th style="text-align:center">Month</th>
              <th>Payment</th>
              <th>Principal</th>
              <th>Interest</th>
              <th>Extra</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <p class="footer">Generated by LandMortgageCalculator.com</p>
      </body>
      </html>
    `)
    printWindow.document.close()
    printWindow.print()
  }

  const handleShare = () => {
    const url = new URL(window.location.href)
    url.searchParams.set("price", results.loanAmount.toString())
    url.searchParams.set("down", results.downPaymentAmount.toString())
    url.searchParams.set("rate", ((results.totalInterest / results.loanAmount) * 100).toFixed(1))
    url.searchParams.set("months", results.actualMonths.toString())

    navigator.clipboard.writeText(url.toString()).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden" ref={tableRef}>
      {/* Header */}
      <div className="px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100">
        <h3
          className="text-xl font-bold text-gray-900"
          style={{ fontFamily: "'DM Serif Display', serif" }}
        >
          Amortization Schedule
        </h3>
        <div className="flex gap-2 no-print">
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-800 rounded-lg text-sm font-semibold hover:bg-emerald-100 transition-colors border border-emerald-200"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-4 py-2 bg-slate-50 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-100 transition-colors border border-slate-200"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-4 h-4" />}
            {copied ? "Copied!" : "Share"}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wider">
              <th className="px-4 py-3 text-center font-semibold">Month</th>
              <th className="px-4 py-3 text-right font-semibold">Payment</th>
              <th className="px-4 py-3 text-right font-semibold">Principal</th>
              <th className="px-4 py-3 text-right font-semibold">Interest</th>
              <th className="px-4 py-3 text-right font-semibold">Extra</th>
              <th className="px-4 py-3 text-right font-semibold">Balance</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, i) => {
              const progressPercent =
                ((results.loanAmount - row.balance) / results.loanAmount) * 100
              return (
                <tr
                  key={row.month}
                  className={`border-b border-gray-50 hover:bg-emerald-50/50 transition-colors ${
                    i % 2 === 0 ? "bg-white" : "bg-gray-50/30"
                  }`}
                >
                  <td className="px-4 py-3 text-center font-medium text-gray-700">
                    <div className="flex items-center justify-center gap-2">
                      <span>{row.month}</span>
                      <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden hidden sm:block">
                        <div
                          className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${progressPercent}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(row.payment)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-medium">
                    {formatCurrency(row.principal)}
                  </td>
                  <td className="px-4 py-3 text-right text-amber-700 font-medium">
                    {formatCurrency(row.interest)}
                  </td>
                  <td className="px-4 py-3 text-right text-blue-600 font-medium">
                    {formatCurrency(row.extraPayment)}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">
                    {formatCurrency(row.balance)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Expand/Collapse */}
      {results.schedule.length > 12 ? (
        <div className="px-6 py-3 border-t border-gray-100 no-print">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-emerald-700 font-semibold text-sm hover:text-emerald-900 transition-colors mx-auto"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" /> Show Less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" /> Show All {results.schedule.length} Months
              </>
            )}
          </button>
        </div>
      ) : null}
    </div>
  )
}
